// src/services/payment.ts
import axios from 'axios';
import type { PaymentDetails, PaymentHistory, Payment } from '../types/payment';
import { getStoredAuthToken } from '../utils/authStorage';

const STORAGE_KEY = 'tellurvision_auth';

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (typeof window !== 'undefined'
    ? `${window.location.protocol}//${window.location.hostname}:5001`
    : '');

const API_URL = `${API_BASE.replace(/\/$/, '')}/api/payments`;

const authHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {};
  const token = getStoredAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

type PaymentPayload = PaymentDetails | Record<string, unknown>;

function extractAxiosMessage(error: unknown): string | null {
  if (typeof error !== 'object' || error === null) return null;
  const e = error as { response?: { data?: { message?: unknown } } };
  const msg = e.response?.data?.message;
  return typeof msg === 'string' ? msg : null;
}

const withAuthDefaults = (input: PaymentPayload): Record<string, unknown> => {
  const payload: Record<string, unknown> = { ...(input as Record<string, unknown>) };

  // Attach userId from local auth storage when available so the
  // backend can associate payments to an account.
  if (typeof window !== 'undefined') {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as { user?: { _id?: string; id?: string } };
        const authUser = parsed?.user;
        const userId =
          (authUser && typeof authUser._id === 'string' && authUser._id) ||
          (authUser && typeof authUser.id === 'string' && authUser.id) ||
          undefined;
        if (userId && typeof payload.userId !== 'string') {
          payload.userId = userId;
        }
      }
    } catch {
      // ignore storage errors; payment can still proceed without user linkage
    }
  }

  // Ensure currency/paymentMethod/status fields exist for the backend
  if (typeof payload.currency !== 'string') {
    payload.currency = 'USD';
  }
  if (typeof payload.paymentMethod !== 'string') {
    payload.paymentMethod = 'credit_card';
  }
  if (typeof payload.status !== 'string') {
    payload.status = 'pending';
  }

  return payload;
};

interface RawPaymentDoc {
  id?: string;
  _id?: string;
  amount?: number;
  currency?: string;
  status?: string;
  userId?: string;
  campaignPackageId?: string;
  campaignPackageName?: string;
  campaignAdId?: string;
  clientSecret?: string;
  stripePaymentIntentId?: string;
  stripePaymentMethodId?: string;
  webhookVerifiedAt?: Date | string;
  createdAt?: Date | string;
  date?: string | Date;
  details?: PaymentDetails;
}

const shapePayment = (raw: RawPaymentDoc | null | undefined): Payment => {
  return {
    id: (raw && (raw.id || raw._id)) || '',
    _id: raw?._id,
    amount: typeof raw?.amount === 'number' ? raw.amount : undefined,
    currency: typeof raw?.currency === 'string' ? raw.currency : undefined,
    status:
      raw?.status === 'pending' || raw?.status === 'completed' || raw?.status === 'failed'
        ? (raw.status as 'pending' | 'completed' | 'failed')
        : undefined,
    userId: typeof raw?.userId === 'string' ? raw.userId : undefined,
    campaignPackageId:
      typeof raw?.campaignPackageId === 'string' ? raw.campaignPackageId : undefined,
    campaignPackageName:
      typeof raw?.campaignPackageName === 'string' ? raw.campaignPackageName : undefined,
    campaignAdId: typeof raw?.campaignAdId === 'string' ? raw.campaignAdId : undefined,
    clientSecret: typeof raw?.clientSecret === 'string' ? raw.clientSecret : undefined,
    stripePaymentIntentId:
      typeof raw?.stripePaymentIntentId === 'string' ? raw.stripePaymentIntentId : undefined,
    stripePaymentMethodId:
      typeof raw?.stripePaymentMethodId === 'string' ? raw.stripePaymentMethodId : undefined,
    webhookVerifiedAt: raw?.webhookVerifiedAt,
    createdAt: raw?.createdAt,
    date: raw?.date,
    details: raw?.details,
  };
};

export const hasCompletedPaymentForAd = async (adId: string): Promise<boolean> => {
  if (!adId) {
    return false;
  }

  const history = await getPaymentHistory();
  return history.some(
    (payment) => payment.status === 'completed' && payment.campaignAdId === adId,
  );
};

// Function to process a payment
export const processPayment = async (paymentDetails: PaymentPayload): Promise<Payment> => {
  try {
    const body = withAuthDefaults(paymentDetails);
    const response = await axios.post(`${API_URL}/process`, body, { headers: authHeaders() });
    return shapePayment(response.data);
  } catch (error) {
    const msg = extractAxiosMessage(error) ?? 'Payment processing failed. Please try again.';
    throw new Error(msg);
  }
};

// Function to retrieve payment history
export const getPaymentHistory = async (userId?: string): Promise<PaymentHistory[]> => {
  try {
    let effectiveUserId = userId;
    if (!effectiveUserId && typeof window !== 'undefined') {
      try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw) as { user?: { _id?: string; id?: string } };
          const authUser = parsed?.user;
          effectiveUserId =
            (authUser && typeof authUser._id === 'string' && authUser._id) ||
            (authUser && typeof authUser.id === 'string' && authUser.id) ||
            undefined;
        }
      } catch {
        // ignore storage issues; fall back to no user filter
      }
    }

    const endpoint = effectiveUserId
      ? `${API_URL}/history?userId=${encodeURIComponent(effectiveUserId)}`
      : `${API_URL}/history`;

    const response = await axios.get(endpoint, { headers: authHeaders() });
    const raw = Array.isArray(response.data) ? response.data : [];
    return raw.map((item) => shapePayment(item as RawPaymentDoc)) as PaymentHistory[];
  } catch (error) {
    const msg = extractAxiosMessage(error) ?? 'Failed to retrieve payment history. Please try again.';
    throw new Error(msg);
  }
};
